import React, { useState } from "react"
import { useDispatch } from "react-redux"
import * as Yup from "yup"
import { Form, Formik } from "formik"
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"
import { updateDistributionCenterLink } from "../../../../actions/Organizations/Enterprise"

const validationSchema = Yup.object({
  packagingNotes: Yup.string().max(1000, "1000 caractères max"),
  deliveryScheduleNotes: Yup.string().max(500, "500 caractères max"),
  minimumDeliveryVolume: Yup.number()
    .integer("Veuillez saisir un nombre entier positif")
    .min(0, "Veuillez saisir un nombre entier positif")
    .nullable()
    .transform((v, orig) => (orig === "" ? null : v)),
})

const sx = {
  dialog: {
    "& .MuiDialog-paper": {
      width: 640,
      maxWidth: "calc(100vw - 32px)",
      borderRadius: "8px",
    },
  },
  title: {
    px: 5,
    pt: 3,
    pb: 1,
    fontSize: 18,
    fontWeight: 500,
    pr: 8,
    marginBottom: "20px !important",
  },
  closeBtn: {
    position: "absolute",
    right: 16,
    top: 16,
    color: "text.secondary",
  },
  content: {
    px: 5,
    pt: 1,
    pb: 2,
  },
  dcInfoBlock: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
    pb: 1,
    borderBottom: "1px solid",
    borderColor: "divider",
  },
  dcName: {
    fontSize: 16,
    fontWeight: 400,
    color: "text.secondary",
  },
  dcCode: {
    fontSize: 12,
    color: "text.secondary",
  },
  field: {
    display: "flex",
    flexDirection: "column",
  },
  fieldLabel: {
    fontSize: 12,
    fontWeight: 400,
    color: "#757575",
    mb: 1,
  },
  fieldTextarea: {
    "& .MuiOutlinedInput-root": {
      bgcolor: "#ffffff",
      fontSize: 14,
      "& fieldset": { borderColor: "#E0E0E0" },
      "&:hover fieldset": { borderColor: "#9E9E9E" },
    },
  },
  fieldVolumeInput: {
    "& .MuiInput-root": { fontSize: 14 },
    "& .MuiInput-underline:before": { borderBottomColor: "#E0E0E0" },
    "& .MuiInput-underline:hover:not(.Mui-disabled):before": { borderBottomColor: "#9E9E9E" },
  },
  serverError: {
    px: 5,
    pb: 1,
    fontSize: 14,
    color: "#B71C1C",
  },
  actions: {
    px: 5,
    pt: 3,
    pb: 4,
    justifyContent: "space-between",
    borderTop: "1px solid",
    borderColor: "divider",
  },
}

const EditDCParamsModal = ({ open, onClose, enterpriseId, link }) => {
  const dispatch = useDispatch()
  const [serverError, setServerError] = useState(null)

  const initialValues = {
    packagingNotes: link.packagingNotes || "",
    deliveryScheduleNotes: link.deliveryScheduleNotes || "",
    minimumDeliveryVolume: link.minimumDeliveryVolume > 0 ? String(link.minimumDeliveryVolume) : "",
  }

  const handleSubmit = async (values, { setSubmitting }) => {
    setServerError(null)
    try {
      await dispatch(updateDistributionCenterLink(enterpriseId, {
        ...link,
        packagingNotes: values.packagingNotes,
        deliveryScheduleNotes: values.deliveryScheduleNotes,
        minimumDeliveryVolume: values.minimumDeliveryVolume === "" ? 0 : parseInt(values.minimumDeliveryVolume, 10),
      }))
      onClose()
    } catch (e) {
      setServerError("Une erreur est survenue, veuillez réessayer")
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} sx={sx.dialog}>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ values, errors, handleChange, isSubmitting, dirty, isValid }) => (
          <Form>
            <DialogTitle sx={sx.title}>
              Modifier — {link.dcName}
              <IconButton onClick={onClose} disabled={isSubmitting} sx={sx.closeBtn}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </DialogTitle>

            <DialogContent sx={sx.content}>
              <Stack spacing={3}>
                <Box sx={sx.dcInfoBlock}>
                  <Typography sx={sx.dcName}>{link.dcName}</Typography>
                  {link.dcCode && (
                    <Typography sx={sx.dcCode}>{link.dcCode}</Typography>
                  )}
                </Box>

                <Box sx={sx.field}>
                  <Typography sx={sx.fieldLabel}>Notes packaging</Typography>
                  <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    size="small"
                    name="packagingNotes"
                    value={values.packagingNotes}
                    onChange={handleChange}
                    error={Boolean(errors.packagingNotes)}
                    helperText={errors.packagingNotes}
                    inputProps={{ maxLength: 1000 }}
                    // eslint-disable-next-line quotes
                    placeholder={`ex : "pas de fourreau", "cartons uniquement"`}
                    sx={sx.fieldTextarea}
                  />
                </Box>

                <Box sx={sx.field}>
                  <Typography sx={sx.fieldLabel}>Créneaux de livraison</Typography>
                  <TextField
                    fullWidth
                    multiline
                    minRows={3}
                    size="small"
                    name="deliveryScheduleNotes"
                    value={values.deliveryScheduleNotes}
                    onChange={handleChange}
                    error={Boolean(errors.deliveryScheduleNotes)}
                    helperText={errors.deliveryScheduleNotes}
                    inputProps={{ maxLength: 500 }}
                    // eslint-disable-next-line quotes
                    placeholder={`ex : "Frais 5h-7h / Épicerie 5h-11h, lundi-samedi"`}
                    sx={sx.fieldTextarea}
                  />
                </Box>

                <Box sx={{ maxWidth: 260 }}>
                  <Box sx={sx.field}>
                    <Typography sx={sx.fieldLabel}>Volume min. par livraison</Typography>
                    <TextField
                      fullWidth
                      variant="standard"
                      name="minimumDeliveryVolume"
                      value={values.minimumDeliveryVolume}
                      onChange={handleChange}
                      error={Boolean(errors.minimumDeliveryVolume)}
                      helperText={errors.minimumDeliveryVolume}
                      inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                      placeholder="0"
                      InputProps={{
                        endAdornment: <InputAdornment position="end">/ plats</InputAdornment>,
                      }}
                      sx={sx.fieldVolumeInput}
                    />
                  </Box>
                </Box>
              </Stack>
            </DialogContent>

            {serverError && (
              <Typography sx={sx.serverError}>{serverError}</Typography>
            )}

            <DialogActions sx={sx.actions}>
              <Button variant="text" onClick={onClose} disabled={isSubmitting}>
                Annuler
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={isSubmitting || !dirty || !isValid}
                startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : null}
              >
                {isSubmitting ? "Enregistrement…" : "Enregistrer"}
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  )
}

export default EditDCParamsModal
